import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useProjectCompletionConfirmation } from "@/components/ProjectCompletionDialog";
import { useProjectClosureConfirmation } from "@/components/ProjectClosureDialog";
import { Download, FileUp, History, ShieldCheck, ArrowLeftRight } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { formatTimestamp } from "@/utils/datetime";
import { saveBlob } from "@/native/files";
import { newestFirst } from "@/utils/sortHistory";


const API = `${process.env.REACT_APP_BACKEND_URL}/api/project-transfer`;


function TransferContainer({ embedded, open, setOpen, children }) {
  if (embedded) return <div>{children}</div>;
  return <Dialog onOpenChange={setOpen} open={open}><DialogContent data-testid="project-transfer-dialog" style={{ maxWidth: 900, maxHeight: "90vh", overflowY: "auto" }}><DialogHeader><DialogTitle>Project transfer &amp; import history</DialogTitle></DialogHeader>{children}</DialogContent></Dialog>;
}

export function ProjectTransferPanel({ canTransfer, projectId, embedded = false, onBusyChange }) {
  const [open, setOpen] = useState(embedded);
  const [file, setFile] = useState(null);
  const [review, setReview] = useState(null);
  const [isWorking, setIsWorking] = useState(false);
  const { requestCompletion, completionDialog } = useProjectCompletionConfirmation();
  const { requestClosure, closureDialog, closureBusy } = useProjectClosureConfirmation(
    open ? `${projectId}:${review?.import_id || ""}` : null,
  );
  useEffect(() => { onBusyChange?.(isWorking || closureBusy); return () => onBusyChange?.(false); }, [isWorking, closureBusy, onBusyChange]);
  const [history, setHistory] = useState([]);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);

  const loadHistory = useCallback(async () => {
    setIsHistoryLoading(true);
    try {
      const response = await axios.get(`${API}/projects/${projectId}/import-history`);
      setHistory(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Import history could not be loaded.");
    } finally {
      setIsHistoryLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    if (open) loadHistory();
  }, [loadHistory, open]);

  if (!canTransfer) return null;

  const download = async (format) => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}/export`, {
        params: { format },
        responseType: "blob",
      });
      await saveBlob(response.data, `project-${projectId}.${format === "xlsx" ? "xlsx" : "zip"}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Export could not be generated.");
    }
  };

  const validateImport = async (event) => {
    event.preventDefault();
    if (!file) return;
    setIsWorking(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const validated = await axios.post(
        `${API}/projects/${projectId}/import/validate`,
        formData,
      );
      const reviewed = await axios.get(
        `${API}/projects/${projectId}/import/${validated.data.import_id}`,
      );
      setReview(reviewed.data);
      toast.success("Import staged for review.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Import could not be validated.");
    } finally {
      setIsWorking(false);
    }
  };

  const approveImport = async (confirmed = false, closureSummary = null) => {
    const validStages = review.stages.filter((stage) => !stage.errors.length);
    const validStageIds = validStages.map((stage) => stage.id);
    if (!validStageIds.length) return;
    const completesProject = !lifecycleLocked && validStages.some((stage) =>
      stage.entity === "project" &&
      stage.changed_fields.includes("lifecycle_state") &&
      stage.incoming?.lifecycle_state === "complete",
    );
    if (completesProject && confirmed !== true) {
      requestCompletion(() => approveImport(true));
      return;
    }
    const closesProject = !lifecycleLocked && validStages.some((stage) =>
      stage.entity === "project" &&
      stage.changed_fields.includes("lifecycle_state") &&
      stage.incoming?.lifecycle_state === "closed",
    );
    if (closesProject && !closureSummary) {
      requestClosure({
        loadPreview: async () => {
          const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/operations/projects/${projectId}/closure-preview`);
          return response.data;
        },
        close: (summary) => approveImport(confirmed, summary),
      });
      return;
    }
    setIsWorking(true);
    try {
      await axios.post(`${API}/projects/${projectId}/import/${review.import_id}/approve`, {
        stage_ids: validStageIds,
        ...(completesProject ? { confirmed_completion: true } : {}),
        ...(closesProject ? { confirmed_closure: true, closure_preview_token: closureSummary.preview_token } : {}),
      });
      toast.success("Reviewed project updates applied.");
      setReview(null);
      setFile(null);
      await loadHistory();
    } catch (error) {
      if (closesProject) throw error;
      toast.error(error.response?.data?.detail || "Reviewed import could not be approved.");
      if (error.response?.status === 409) {
        setReview(null);
        await loadHistory();
      }
    } finally {
      setIsWorking(false);
    }
  };

  const validCount = review?.stages.filter((stage) => !stage.errors.length).length || 0;
  const errorCount = review?.stages.filter((stage) => stage.errors.length).length || 0;
  const lifecycleLocked = history.some((entry) => entry.lifecycle_applied);

  return (
    <>
      {!embedded && <button
        className="table-edit-button"
        data-testid="project-transfer-open-button"
        onClick={() => setOpen(true)}
        style={{ display: "inline-flex", alignItems: "center", gap: "0.35rem" }}
        type="button"
      >
        <ArrowLeftRight size={14} /> Transfer & Imports
      </button>}
      <TransferContainer embedded={embedded} open={open} setOpen={setOpen}>
          <section className="project-transfer-panel" data-testid="project-transfer-panel">
      <div>
        <span className="operations-kicker">Project transfer</span>
        <h2>Export and reviewed import</h2>
        <p>
          Export this project’s records, edit only the exported template, then validate every
          proposed update before approval.
        </p>
      </div>
      <div className="project-transfer-actions">
        <button data-testid="project-export-xlsx-button" onClick={() => download("xlsx")} type="button">
          <Download size={15} /> Export Excel to edit and re-import
        </button>
        <button data-testid="project-export-csv-button" onClick={() => download("csv")} type="button">
          <Download size={15} /> Export CSV to edit and re-import
        </button>
      </div>
      <form data-testid="project-import-form" onSubmit={validateImport}>
        <input
          accept=".xlsx,.zip"
          data-testid="project-import-file-input"
          disabled={isWorking || closureBusy}
          onChange={(event) => { setFile(event.target.files?.[0] || null); setReview(null); }}
          required
          type="file"
        />
        <button data-testid="project-import-validate-button" disabled={!file || isWorking || closureBusy} type="submit">
          <FileUp size={15} /> Validate and stage
        </button>
      </form>
      {review && (
        <div className="project-transfer-review" data-testid="project-import-review">
          <strong><ShieldCheck size={15} /> {validCount} reviewed updates ready</strong>
          <span>{errorCount} rows need correction before a new import can be staged.</span>
          <ul>
            {review.stages.map((stage) => (
              <li data-testid={`project-import-stage-${stage.id}`} key={stage.id}>
                {stage.entity} row {stage.row_number}: {stage.errors.length ? stage.errors.join(", ") : stage.changed_fields.join(", ") || "no changes"}
              </li>
            ))}
          </ul>
          <button
            data-testid="project-import-approve-button"
            disabled={!validCount || isWorking || closureBusy || errorCount > 0}
            onClick={approveImport}
            type="button"
          >
            Approve reviewed updates
          </button>
        </div>
      )}
      <section className="project-transfer-history" data-testid="project-import-history">
        <div className="project-transfer-history-heading">
          <div>
            <span className="operations-kicker">Import History</span>
            <h3>Approved imports</h3>
          </div>
          <button
            data-testid="project-import-history-refresh-button"
            disabled={isHistoryLoading}
            onClick={loadHistory}
            type="button"
          >
            <History size={15} /> Refresh history
          </button>
        </div>
        <p>
          Original files are processed transiently and are not retained. This audit history remains
          after import approval.
        </p>
        <div
          className={`project-import-lifecycle-lock ${lifecycleLocked ? "is-locked" : ""}`}
          data-testid="project-import-lifecycle-lock"
        >
          <strong>{lifecycleLocked ? "Lifecycle import locked" : "Lifecycle import available"}</strong>
          <span>
            {lifecycleLocked
              ? "A prior approved import set lifecycle/status. Future imports cannot override it."
              : "The first approved import with lifecycle/status changes may set them once."}
          </span>
        </div>
        {isHistoryLoading && <p data-testid="project-import-history-loading">Loading history…</p>}
        {!isHistoryLoading && !history.length && (
          <p data-testid="project-import-history-empty">No approved imports recorded yet.</p>
        )}
        <ol>
          {newestFirst(history, "approved_at").map((entry) => (
            <li data-testid={`project-import-history-${entry.id}`} key={entry.id}>
              <strong>{entry.source_filename}</strong>
              <span>{formatTimestamp(entry.approved_at)} · {entry.rows} reviewed rows</span>
              <small>
                {entry.lifecycle_applied
                  ? "Lifecycle/status applied and now locked for later imports."
                  : "Lifecycle/status remains under normal app workflow."}
              </small>
              <em>
                Changed fields: {entry.changed_fields.length ? entry.changed_fields.join(", ") : "none"}
              </em>
            </li>
          ))}
        </ol>
      </section>
    </section>
          {completionDialog}
          {closureDialog}
      </TransferContainer>
    </>
  );
}

